import { atom } from 'jotai';
import { competitionIdsToFilterByAtom } from '@/atoms/filters/highLevel/competitions';
import { seasonIdsToFilterByAtom } from '@/atoms/filters/highLevel/seasons';
import { positionPercentagesToMinutes } from '@/utils/player';
import { objSnakeToCamel } from '@/utils/queries';
import { playerCyclesOrderedAtom } from '../players';
import { playerAggsMinutesPlayedWithFallbackAtom } from './playerMinsPlayed';
import { rawPlayerPositionsAtom } from './playerPositions';
import { rawPlayerRadarDataAtom } from './playerRadarData';

export type PlayerAiSummaryFilterParams = {
  competitionInfos: { id: number; name: string | null; country: string | null }[];
  seasonInfos: { id: number; name: string | null }[];
};

export const playerAiSummaryFilterParamsAtom = atom(async get => {
  const playerCycles = await get(playerCyclesOrderedAtom);
  const seasonIds = get(seasonIdsToFilterByAtom);
  const competitionIds = get(competitionIdsToFilterByAtom);

  const seasonIdSet = new Set(seasonIds);
  const competitionIdSet = new Set(competitionIds);

  const cycles = playerCycles.filter(
    ({ season, competition }) =>
      (!seasonIdSet.size || seasonIdSet.has(season.seasonId)) &&
      (!competitionIdSet.size || competitionIdSet.has(competition.competitionId)),
  );

  if (!cycles.length) return null;

  const competitionInfos = [
    ...new Map(
      cycles.map(({ competition: { competitionId: id, name, areaCode } }) => [id, { id, name, country: areaCode }]),
    ).values(),
  ];

  const seasonInfos = [...new Map(cycles.map(({ season: { seasonId: id, name } }) => [id, { id, name }])).values()];

  return { competitionInfos, seasonInfos };
});

// positions[0] is primaryPosition — first after percentage-desc sort is used for percentile lookup
export const playerSummaryPositionsAtom = atom(async get => {
  const raw = await get(rawPlayerPositionsAtom);
  const positions = raw.data?.map(objSnakeToCamel) ?? [];
  return [...positions].sort((a, b) => b.percentage - a.percentage).map(pos => pos.position);
});

// Per-position raw minutes for the current filter context. Paired with playerSummaryPositionsAtom so
// the agent can check whether the player has enough minutes in the selected profile's position group.
export const playerSummaryPositionMinutesAtom = atom(async get => {
  const [raw, totalMinutes] = await Promise.all([
    get(rawPlayerPositionsAtom),
    get(playerAggsMinutesPlayedWithFallbackAtom),
  ]);
  const positions = raw.data?.map(objSnakeToCamel) ?? [];
  return positionPercentagesToMinutes(positions, totalMinutes);
});

export const playerSummaryAggregatesAtom = atom(async get => {
  const raw = await get(rawPlayerRadarDataAtom);
  return raw.data ?? {};
});
